import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { createCalendarTask, getCustomers, getServerErrorMessage } from "@api";
import { usePaymentSettings } from "@auth";
import { toast } from "@components/toast/Toast";
import { usePaymentRequestTemplatesContext } from "@context/PaymentRequestTemplatesContext";
import { buildApiQuery } from "@utils";
import BatchTemplateRequestModal from "@areas/trade-accounts/flows/BatchTemplateRequestModal";
import {
  buildSuggestedRequestPayload,
  templateToRequestConfig,
  withoutSmsForStatements,
} from "@areas/trade-accounts/flows/BatchTemplateRequestModal/utils/buildSuggestionRequest";
import { sendBatch } from "@areas/trade-accounts/flows/PaymentRequestDrawers/utils/sendBatch";
import EventForm from "@areas/calendar/flows/EventForm";
import { useDocumentActions } from "../../portal-template/components/SmartNoteModalV2/documentActions";

const tomorrowAtNine = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(9, 0, 0, 0);
  return d;
};

// Accounts per lookup — the ids ride the query string, so a big group is fetched in batches.
const IDS_PER_LOOKUP = 100;

// The step's accounts as Trade Accounts rows (the shape the request and document flows expect).
const fetchRows = async (suggestion) => {
  const ids = suggestion.customerIds ?? (suggestion.customerId ? [suggestion.customerId] : []);
  const batches = [];
  for (let i = 0; i < ids.length; i += IDS_PER_LOOKUP) batches.push(ids.slice(i, i + IDS_PER_LOOKUP));
  const pages = await Promise.all(
    batches.map((batch) => getCustomers(buildApiQuery({ page: 1, pageSize: batch.length, customerIds: batch }))),
  );
  return pages.flatMap((page) => page.data ?? []);
};

const fetchAccountRow = async (suggestion) => (await fetchRows(suggestion))[0] ?? null;

// The documents a SendDocuments step names, as document rows: each invoice (and its proof of delivery
// when asked for), or the account's statement when it names none.
const documentRows = ({ invoiceNumbers, includePods }) => {
  if (!invoiceNumbers?.length) {
    return [{ id: "statement", title: "Account statement", spec: { documentType: "Statement" } }];
  }
  return invoiceNumbers.flatMap((number) => [
    { id: `inv-${number}`, title: `Invoice ${number}`, spec: { documentType: "Invoice", invoiceNumbers: [number] } },
    ...(includePods
      ? [{ id: `pod-${number}`, title: `POD for ${number}`, spec: { documentType: "ProofOfDelivery", invoiceNumbers: [number] } }]
      : []),
  ]);
};

// Mounted for one documents step: generates its documents and opens the send-documents modal straight away.
// `onUnavailable` instead of `onClose` when the modal never opened because nothing could be generated.
const DocumentsSend = ({ customer, suggestion, onSent, onClose, onUnavailable }) => {
  const preparing = useRef(true);
  const actions = useDocumentActions({
    customer,
    onSent,
    onClose: () => (preparing.current ? onUnavailable() : onClose()),
  });
  useEffect(() => {
    actions.openSendFor(documentRows(suggestion), "plan-of-action").finally(() => {
      preparing.current = false;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return actions.sendModal;
};

/**
 * Acts on a plan-of-action suggestion. `send` is the one click: a payment request goes straight out
 * with the merchant template the step names (the same headless path a previewed request confirms
 * through), documents open the send-documents modal ready to send, and a task step is simply marked
 * done, since the step already is the to-do. `secondary` is the hover option: Preview for a request (its
 * rendered message), or Create a task for a task step that's for later or someone else, through the
 * full task form. `onDone(suggestion, outcome, details)` fires once something was actually done, with a line
 * saying what happened; `details.result` says which (plus sent/attempted counts for requests). `onFail(suggestion, reason)` fires
 * when an action couldn't go ahead, and `onAbandon(suggestion, flow)` when the task form or documents modal is
 * closed without finishing.
 *
 * Returns `send`, `secondary`, `secondaryLabel`, the id of the step in flight, whether a flow is
 * showing, and the flow `elements` to render once.
 */
export const useSuggestionAction = ({ onDone, onFail, onAbandon }) => {
  const queryClient = useQueryClient();
  const { paymentSettings } = usePaymentSettings();
  const { templates, getTemplateById } = usePaymentRequestTemplatesContext();
  const [pending, setPending] = useState(null);
  const [requestPreview, setRequestPreview] = useState(null);
  const [taskDraft, setTaskDraft] = useState(null);
  const [documents, setDocuments] = useState(null);

  const requestConfigFor = (suggestion) => {
    const template =
      (suggestion.templateId && getTemplateById(suggestion.templateId)) ??
      templates.find((t) => t.isSystemDefault) ??
      templates[0];
    return template ? withoutSmsForStatements(templateToRequestConfig(template)) : null;
  };

  const withPending = async (suggestion, fn) => {
    setPending(suggestion.id);
    try {
      await fn();
    } catch (error) {
      toast({ description: getServerErrorMessage(error) || "Something went wrong — please try again." });
      onFail?.(suggestion, "error");
    } finally {
      setPending(null);
    }
  };

  const loadRows = async (suggestion) => {
    const rows = await fetchRows(suggestion);
    if (rows.length === 0) {
      toast({ description: "No accounts to send to right now." });
      onFail?.(suggestion, "no_accounts");
    }
    return rows;
  };

  const sendRequests = (suggestion) =>
    withPending(suggestion, async () => {
      const requestConfig = requestConfigFor(suggestion);
      if (!requestConfig) {
        toast({ description: "Set up a payment request template to send this." });
        onFail?.(suggestion, "no_template");
        return;
      }
      const rows = await loadRows(suggestion);
      if (rows.length === 0) return;

      const { rows: requests, strategy, meta } = buildSuggestedRequestPayload(rows, requestConfig);
      const result = await sendBatch({ rows: requests, strategy, meta, paymentSettings });
      const sent = result?.totals?.succeeded ?? 0;
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      if (sent === 0) {
        toast({ description: "Couldn't send — check the accounts have an email or mobile." });
        onFail?.(suggestion, "no_contact_details");
        return;
      }
      const outcome =
        sent === rows.length
          ? `Sent ${sent === 1 ? "a payment request" : `${sent} payment requests`}`
          : `Sent ${sent} of ${rows.length} payment requests — the rest had no way to reach them`;
      toast({ description: outcome });
      onDone?.(suggestion, outcome, { result: "payment_requests_sent", sentCount: sent, attemptedCount: rows.length });
    });

  const createTask = (task, suggestion) =>
    withPending(suggestion, async () => {
      await createCalendarTask(task);
      queryClient.invalidateQueries({ queryKey: ["caldav"] });
      queryClient.invalidateQueries({ queryKey: ["todaysTasks"] });
      toast({ description: "Task created" });
      setTaskDraft(null);
      onDone?.(suggestion, "Task created", { result: "task_created" });
    });

  const openDocuments = (suggestion) =>
    withPending(suggestion, async () => {
      const row = await fetchAccountRow(suggestion);
      if (!row) {
        toast({ description: "Couldn't find that account." });
        onFail?.(suggestion, "account_not_found");
        return;
      }
      setDocuments({ suggestion, customer: row });
    });

  const send = (suggestion) => {
    if (suggestion.kind === "SendPaymentRequest") return sendRequests(suggestion);
    if (suggestion.kind === "SendDocuments") return openDocuments(suggestion);
    onDone?.(suggestion, "Marked done", { result: "marked_done" });
  };

  const secondary = (suggestion) => {
    if (suggestion.kind === "CreateTask") {
      setTaskDraft({ suggestion });
      return;
    }
    if (suggestion.kind !== "SendPaymentRequest") return;
    withPending(suggestion, async () => {
      const requestConfig = requestConfigFor(suggestion);
      if (!requestConfig) {
        toast({ description: "Set up a payment request template to send this." });
        onFail?.(suggestion, "no_template");
        return;
      }
      const rows = await loadRows(suggestion);
      if (rows.length > 0) setRequestPreview({ rows, requestConfig });
    });
  };

  const draft = taskDraft?.suggestion;
  const elements = (
    <>
      <BatchTemplateRequestModal
        readOnly
        skipTemplateSelect
        isOpen={!!requestPreview}
        onClose={() => setRequestPreview(null)}
        customerData={requestPreview?.rows ?? []}
        requestConfig={requestPreview?.requestConfig ?? null}
      />
      <EventForm
        isOpen={!!draft}
        onClose={() => {
          onAbandon?.(draft, "task_form");
          setTaskDraft(null);
        }}
        onCreate={(task) => createTask(task, draft)}
        saving={!!draft && pending === draft.id}
        item={draft ? { kind: "task", summary: draft.title, description: draft.rationale, due: tomorrowAtNine() } : null}
        defaultCustomer={
          draft?.customerId
            ? { id: draft.customerId, name: draft.account?.name, accountCode: draft.account?.accountCode }
            : null
        }
      />
      {documents && (
        <DocumentsSend
          key={documents.suggestion.id}
          customer={documents.customer}
          suggestion={documents.suggestion}
          onSent={() => {
            onDone?.(documents.suggestion, "Documents sent", { result: "documents_sent" });
            setDocuments(null);
          }}
          onClose={() => {
            onAbandon?.(documents.suggestion, "documents");
            setDocuments(null);
          }}
          onUnavailable={() => {
            onFail?.(documents.suggestion, "documents_unavailable");
            setDocuments(null);
          }}
        />
      )}
    </>
  );

  return {
    send,
    secondary,
    secondaryLabel: (suggestion) =>
      suggestion.kind === "SendPaymentRequest" ? "Preview" : suggestion.kind === "CreateTask" ? "Create a task" : null,
    pending,
    flowOpen: !!requestPreview || !!taskDraft || !!documents,
    elements,
  };
};
